// Precio real del canal de venta oficial (la ventana de reserva vigente).
// Solo lectura: consulta el mismo calendario público que usa la ventana
// oficial para mostrar exactamente el importe que el cliente pagará.
// No crea carritos, reservas ni pagos.
import { createServerFn } from "@tanstack/react-start";
import { z } from "zod";

const CHANNEL_UUID = "e534306c-6722-4b35-a39b-eb33ed8ade97";
const BASE = `https://widgets.bokun.io/widgets/${CHANNEL_UUID}`;

export interface OfficialChannelPrice {
  currency: string;
  /** Importe por unidad de cada categoría (id → importe). */
  categoryPrices: Record<string, number>;
  firstDate: string | null;
  startTimes: string[];
}

const cache = new Map<string, { at: number; value: OfficialChannelPrice | null }>();
const TTL = 10 * 60_000;

function parseMoney(s: unknown): number | null {
  if (typeof s !== "string") return null;
  // Formato es_ES: "MXN 1.316,25"
  const n = s.replace(/[^0-9,.]/g, "").replace(/\./g, "").replace(",", ".");
  const v = Number(n);
  return Number.isFinite(v) ? v : null;
}

type Day = {
  dateObj?: number;
  activityAvailabilityStatus?: string;
  totalPriceByRate?: {
    priceDetails?: { amount?: number; currency?: string };
    priceByPricingCategory?: { pricingCategoryId?: number; categoryPrice?: string }[];
  }[];
  availabilities?: { activityAvailability?: { startTime?: string } }[];
};

export const getOfficialChannelPrice = createServerFn({ method: "GET" })
  .inputValidator((d) =>
    z
      .object({
        activityId: z.string().regex(/^\d{1,10}$/),
        categoryIds: z.array(z.string().regex(/^\d{1,10}$/)).max(12),
        /** Categorías de precio por grupo (la ventana oficial exige groupSize). */
        groupIds: z.array(z.string().regex(/^\d{1,10}$/)).max(12).optional(),
        /** Moneda mostrada en la página; Bókun devuelve sus propios importes en ella. */
        currency: z.string().regex(/^[A-Z]{3}$/).optional(),
      })
      .parse(d),
  )
  .handler(async ({ data }): Promise<OfficialChannelPrice | null> => {
    const ccy = data.currency ?? "MXN";
    const {assertPublicBokunId}=await import("./public-catalog-guard.server");
    await assertPublicBokunId(data.activityId);
    const {quoteReferencePrice}=await import("./bokun-final-quote.server");
    const categoryPrices:Record<string,number>={};
    for (const id of data.categoryIds) {
      const quote=await quoteReferencePrice(data.activityId,ccy,id).catch(()=>null);
      if (quote) categoryPrices[id]=quote.amount;
    }
    if (data.activityId) return Object.keys(categoryPrices).length ? {currency:ccy,categoryPrices,firstDate:null,startTimes:[]} : null;
    const key = `${ccy}:${data.activityId}:${data.categoryIds.join(",")}:${(data.groupIds ?? []).join(",")}`;
    const hit = cache.get(key);
    if (hit && Date.now() - hit.at < TTL) return hit.value;

    const sessionId = crypto.randomUUID();
    const body = JSON.stringify({
      pricingCategories: data.categoryIds.map((id) =>
        data.groupIds?.includes(id)
          ? { id: Number(id), quantity: 1, groupSize: 1 }
          : { id: Number(id), quantity: 1 },
      ),
    });
    const now = new Date();
    let best: { amount: number; day: Day; rate: NonNullable<Day["totalPriceByRate"]>[number] } | null = null;
    let firstDate: string | null = null;
    const times = new Set<string>();

    for (let i = 0; i < 3 && !best; i++) {
      const d = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth() + i, 1));
      const url = `${BASE}/activity/${data.activityId}/${d.getUTCFullYear()}/${d.getUTCMonth() + 1}?currency=${ccy}&sessionId=${sessionId}&lang=es_ES`;
      try {
        const res = await fetch(url, {
          method: "POST",
          headers: { "content-type": "application/json", accept: "application/json" },
          body,
        });
        if (!res.ok) continue;
        const json = (await res.json()) as { calendar?: { weeks?: { days?: Day[] }[] } };
        for (const w of json.calendar?.weeks ?? []) {
          for (const day of w.days ?? []) {
            if (day.activityAvailabilityStatus !== "AVAILABLE") continue;
            if (!firstDate && day.dateObj) firstDate = new Date(day.dateObj).toISOString().slice(0, 10);
            for (const a of day.availabilities ?? []) {
              const st = a.activityAvailability?.startTime;
              if (st) times.add(st);
            }
            for (const rate of day.totalPriceByRate ?? []) {
              const amt = rate.priceDetails?.amount;
              if (typeof amt === "number" && amt > 0 && (!best || amt < best.amount)) {
                best = { amount: amt, day, rate };
              }
            }
          }
        }
      } catch {
        /* sin datos de este mes */
      }
    }

    let value: OfficialChannelPrice | null = null;
    if (best) {
      const categoryPrices: Record<string, number> = {};
      for (const c of best.rate.priceByPricingCategory ?? []) {
        const v = parseMoney(c.categoryPrice);
        if (c.pricingCategoryId != null && v != null) categoryPrices[String(c.pricingCategoryId)] = v;
      }
      // Una sola categoría: el total del día es el precio de esa unidad.
      if (data.categoryIds.length === 1 && Object.keys(categoryPrices).length === 0) {
        categoryPrices[data.categoryIds[0]] = best.amount;
      }
      value = {
        currency: best.rate.priceDetails?.currency ?? ccy,
        categoryPrices,
        firstDate,
        startTimes: [...times].sort(),
      };
    }
    cache.set(key, { at: Date.now(), value });
    return value;
  });
